import {jsx} from 'panel';
import type {JsxVNodeChildren} from 'snabbdom-jsx-lite';

import {DEFAULT_SEARCH_QUERY_STRING} from 'common/report/util/search';
import {SearchSortOption} from 'common/report/util/search/types';
import {SvgIconEl} from 'common/components/svg-icon';

import {promptFeedbackModal} from '../feedback-modal';
import {FeedbackForFeature} from '../feedback-modal/types';
import {EmptyDashboardCreateHero} from '../warning-hero/presets';
import type {Browse} from './index';

const EmptyState = (
  {title = `No results`, subtitle, icon = `search`}: {title?: string; subtitle?: string; icon?: string},
  detailNodes: JsxVNodeChildren,
) => {
  return (
    <div sel='.browse-empty-state'>
      <div sel='.browse-empty-icon'>
        <SvgIconEl attrs={{icon}} />
      </div>
      <div sel='.browse-empty-title'>{title}</div>
      {subtitle ? <div sel='.browse-empty-subtitle'>{subtitle}</div> : null}
      <div sel='.browse-empty-details'>{detailNodes}</div>
    </div>
  );
};

export const ErrorEmptyState = () => {
  return (
    <EmptyState title='Something happened...' subtitle={`We're thinking this isn't the result you want`} icon='warning'>
      If this error persists, please contact
      {` `}
      <a sel='.mp-link' attrs={{href: `https://mixpanel.com/get-support`, target: `_blank`}}>
        support
      </a>
      , or check our
      {` `}
      <a sel='.mp-link' attrs={{href: `https://www.mixpanelstatus.com/`, target: `_blank`}}>
        status page
      </a>
    </EmptyState>
  );
};

export const NoResultsEmptyState = ({cmp}: {cmp: Browse}) => {
  const hasFilteredResults =
    cmp.params.searchText !== DEFAULT_SEARCH_QUERY_STRING ||
    cmp.state.queryParams.sort === SearchSortOption.RecentlyViewed ||
    cmp.getNumActiveFilters() > 0;

  if (!hasFilteredResults) {
    if (cmp.isFullPageMode()) {
      return (
        <EmptyDashboardCreateHero
          onCreateDashboardClick={cmp.params.onCreateDashboardClicked}
          onOpenTemplateModalClick={cmp.params.onOpenTemplateModalClicked}
          onOpenFoundationalTemplateModalClicked={cmp.params.onOpenFoundationalTemplateModalClicked}
          shouldUseFoundationalTemplates={cmp.params.shouldUseFoundationalTemplates}
          headerText={`Monitor your metrics in one place`}
          bodyText={`Looks like there aren't any boards or reports to browse yet.\nCreate your first board or use a template to get started.`}
        />
      );
    }

    if (cmp.isAddMode()) {
      return (
        <EmptyState
          title={`Looks like there aren't any reports to choose from`}
          subtitle={`Create a new report to add it to a board`}
          icon={`report`}
        />
      );
    }

    return <EmptyState title={`You don't have any saved entities yet.`} />;
  } else {
    if (cmp.isFullPageMode()) {
      return (
        <EmptyState subtitle={`Not finding what you're looking for?`}>
          <a
            sel='.mp-link'
            on={{
              click: () => {
                promptFeedbackModal({feature: FeedbackForFeature.Browse});
              },
            }}
          >
            Let us know what you expected
          </a>
        </EmptyState>
      );
    }

    return <EmptyState subtitle={`You may want to try different keywords or check for possible typos.`} />;
  }
};
